import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { companyService } from "@/features/company/services/companyService";
import { Button } from "@/shared/components/ui/Button";
import { Modal } from "@/shared/components/ui/Modal";
import { Spinner } from "@/shared/components/ui/Spinner";

interface XrefRow {
  sequence_id: number;
  role_name: string | null;
  batch_no: string | null;
  registration_code: string | null;
}

interface Props {
  companyId: string;
  companyName: { en: string; ja?: string };
  onClose: () => void;
}

export const RegistrationCodesModal = ({ companyId, companyName, onClose }: Props) => {
  const { t, i18n } = useTranslation();
  const { userData, roles } = useAuth();
  const userType = userData?.userType?.toUpperCase() || "";
  const canSeeAdmin =
    userType === "ADMIN" ||
    roles.length === 0 ||
    roles.includes("role_management_write");
  const [rows, setRows] = useState<XrefRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const resolvedName =
    (i18n.language !== "en" && companyName[i18n.language as keyof typeof companyName]) ||
    companyName.en ||
    "";

  useEffect(() => {
    setLoading(true);
    setError(false);
    companyService
      .getRegistrationCodes(companyId)
      .then(setRows)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [companyId]);

  const exportCSV = useCallback(() => {
    const headers = [
      t("company.codes.company"),
      t("company.codes.role"),
      t("company.codes.batch"),
      t("company.codes.code"),
    ];
    const escapeCSV = (val: string) => {
      if (val.includes(",") || val.includes('"') || val.includes("\n")) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    };
    const dataRows = rows
      .filter((r) => canSeeAdmin || r.role_name?.toUpperCase() !== "ADMIN")
      .map((r) => [
      resolvedName,
      r.role_name ?? "",
      r.batch_no ?? "",
      r.registration_code ?? "",
    ]);
    const csvContent = [
      headers.map(escapeCSV).join(","),
      ...dataRows.map((r) => r.map(escapeCSV).join(",")),
    ].join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `registration_codes_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [rows, resolvedName, t]);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t("company.codes.title")}
      headerAction={
        !loading && rows.length > 0 ? (
          <Button size="sm" variant="secondary" onClick={exportCSV}>
            {t("company.codes.export_csv")}
          </Button>
        ) : undefined
      }
    >
      <p className="text-xs text-gray-500 mb-4">{resolvedName}</p>

      {loading ? (
        <div className="py-10">
          <Spinner />
        </div>
      ) : error ? (
        <p className="text-sm text-red-500 text-center py-8">{t("company.codes.load_error")}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-8">{t("company.codes.empty")}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide pb-2 pr-6">
                  {t("company.codes.role")}
                </th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide pb-2 pr-6">
                  {t("company.codes.batch")}
                </th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide pb-2">
                  {t("company.codes.code")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows
                .filter((row) => canSeeAdmin || row.role_name?.toUpperCase() !== "ADMIN")
                .map((row) => (
                  <tr key={row.sequence_id} className="hover:bg-gray-50">
                    <td className="py-2 pr-6 text-gray-700 font-medium">{row.role_name ?? "—"}</td>
                    <td className="py-2 pr-6 text-gray-500">{row.batch_no ?? "—"}</td>
                    <td className="py-2 font-mono text-gray-900 select-all">
                      {row.registration_code ?? "—"}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
};
